import React, { useState, useEffect } from 'react';

const AddProjectModal = ({ onClose, onSuccess, authTokens }) => {
  const [formData, setFormData] = useState({
    project_name: '',
    project_manager_id: '',
    resources: []
  });
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ess-api-django-a6fc2c26f42e.herokuapp.com';

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      setManagersLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/timesheet/project-status/managers_list/`, {
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setManagers(data);
        console.log('Managers loaded:', data);
      } else {
        console.error('Failed to fetch managers, status:', response.status);
        setError('Failed to load managers list');
      }
    } catch (err) {
      console.error('Failed to fetch managers:', err);
      setError('Failed to load managers list');
    } finally {
      setManagersLoading(false);
    }
  };

  const addResource = () => {
    setFormData(prev => ({
      ...prev,
      resources: [...prev.resources, { resource_name: '', role: '' }]
    }));
  };

  const removeResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  const updateResource = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.map((resource, i) => 
        i === index ? { ...resource, [field]: value } : resource
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_name.trim()) {
      setError('Project name is required');
      return;
    }
    
    if (!formData.project_manager_id) {
      setError('Please select a project manager');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/timesheet/projects/`, {
        method: 'POST',
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create project');
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '12px',
      maxWidth: '700px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    },
    header: {
      padding: '24px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#111827',
      margin: 0
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      fontSize: '28px',
      cursor: 'pointer',
      color: '#6b7280',
      lineHeight: '1',
      padding: '0',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px'
    },
    body: {
      padding: '24px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    resourcesSection: {
      marginTop: '24px',
      padding: '16px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    resourcesHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    },
    resourcesTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#111827',
      margin: 0
    },
    addResourceBtn: {
      padding: '6px 12px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    resourceItem: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr auto',
      gap: '12px',
      marginBottom: '12px',
      alignItems: 'end'
    },
    removeBtn: {
      padding: '10px',
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '12px',
      borderRadius: '6px',
      fontSize: '14px',
      marginBottom: '16px'
    },
    footer: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      paddingTop: '16px',
      borderTop: '1px solid #e5e7eb'
    },
    btnCancel: {
      padding: '10px 20px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      backgroundColor: 'white',
      color: '#374151'
    },
    btnSubmit: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      backgroundColor: '#3b82f6',
      color: 'white'
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Add New Project</h2>
          <button 
            style={styles.closeBtn}
            onClick={onClose}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            &times;
          </button>
        </div>

        <div style={styles.body}>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Project Name *</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Enter project name"
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Project Manager *</label>
              <select
                style={styles.select}
                value={formData.project_manager_id}
                onChange={(e) => setFormData({ ...formData, project_manager_id: e.target.value })}
                required
                disabled={managersLoading}
              >
                <option value="">
                  {managersLoading ? 'Loading managers...' : 'Select a manager'}
                </option>
                {managers.map(manager => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name}
                  </option>
                ))}
              </select>
              {!managersLoading && managers.length === 0 && (
                <small style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  No managers found. Please create manager users first.
                </small>
              )}
            </div>

            {/* <div style={styles.resourcesSection}>
              <div style={styles.resourcesHeader}>
                <h3 style={styles.resourcesTitle}>Project Resources</h3>
                <button
                  type="button"
                  style={styles.addResourceBtn}
                  onClick={addResource}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                >
                  <span>+</span> Add Resource
                </button>
              </div>

              {formData.resources.map((resource, index) => (
                <div key={index} style={styles.resourceItem}>
                  <div>
                    <label style={{...styles.label, fontSize: '12px'}}>Resource Name</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Resource name"
                      value={resource.resource_name}
                      onChange={(e) => updateResource(index, 'resource_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={{...styles.label, fontSize: '12px'}}>Role</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Role"
                      value={resource.role}
                      onChange={(e) => updateResource(index, 'role', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    style={styles.removeBtn}
                    onClick={() => removeResource(index)}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#fecaca'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#fee2e2'}
                  >
                    ×
                  </button>
                </div>
              ))}
              
              {formData.resources.length === 0 && (
                <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                  No resources added yet
                </p>
              )}
            </div>

            {error && (
              <div style={styles.errorMessage}>
                {error}
              </div>
            )} */}

            <div style={styles.footer}>
              <button 
                type="button" 
                style={styles.btnCancel}
                onClick={onClose}
                disabled={loading}
                onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#f9fafb')}
                onMouseOut={(e) => !loading && (e.target.style.backgroundColor = 'white')}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                style={{
                  ...styles.btnSubmit,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                disabled={loading}
                onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#2563eb')}
                onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#3b82f6')}
              >
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProjectModal;